// v3/app/providers/ToastProvider.tsx — Toast provider using Radix Toast
// Reads toasts from UI store and renders them with auto-dismiss.
// Zero imports from v1/v2.

import React, { useEffect, useRef } from 'react';
import {
  ToastProvider as RadixToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
} from '../../design-system/primitives/toast';
import { useUIStore } from '../../shared/stores/ui-store';

const TOAST_DURATION_MS = 5_000;

/** Maps our store variant to the toast primitive variant */
function mapVariant(variant: string): 'default' | 'destructive' | 'success' {
  switch (variant) {
    case 'success':
      return 'success';
    case 'error':
      return 'destructive';
    default:
      return 'default';
  }
}

interface ToastProviderProps {
  children: React.ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const toasts = useUIStore((state) => state.toasts);
  const removeToast = useUIStore((state) => state.removeToast);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    for (const toast of toasts) {
      if (!timersRef.current.has(toast.id)) {
        const timer = setTimeout(() => {
          removeToast(toast.id);
          timersRef.current.delete(toast.id);
        }, TOAST_DURATION_MS);
        timersRef.current.set(toast.id, timer);
      }
    }

    // Clean up timers for removed toasts
    const currentIds = new Set(toasts.map((t) => t.id));
    for (const [id, timer] of timersRef.current.entries()) {
      if (!currentIds.has(id)) {
        clearTimeout(timer);
        timersRef.current.delete(id);
      }
    }
  }, [toasts, removeToast]);

  // Clean up all timers on unmount
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) {
        clearTimeout(timer);
      }
      timers.clear();
    };
  }, []);

  return (
    <RadixToastProvider>
      {children}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          variant={mapVariant(toast.variant)}
          onOpenChange={(open) => {
            if (!open) removeToast(toast.id);
          }}
        >
          <div className="grid gap-1">
            <ToastTitle>{toast.title}</ToastTitle>
            {toast.description && (
              <ToastDescription>{toast.description}</ToastDescription>
            )}
          </div>
          <ToastClose />
        </Toast>
      ))}
      <ToastViewport />
    </RadixToastProvider>
  );
}
